<!-- src/routes/[type]/[slug]/+page.svelte -->
<script>
	import { onMount } from 'svelte';
	import Wheel from '$lib/components/Wheel.svelte';
	import NameInput from '$lib/components/NameInput.svelte';
	import { wheelStore } from '$lib/store/wheelStore';
	import ShareButtons from '$lib/components/ShareButtons.svelte';
	import WinnerPopup from '$lib/components/WinnerPopup.svelte';

	export let data = {};
	
	// Function to validate and sanitize page data
	function getSafePageData() {
		if (!data || !data.pageData) {
			// Return default data if the pageData doesn't exist
			return {
				title: 'Random Name Picker Wheel | Free Online Spinner Tool',
				metaDescription: 'Free online random name picker wheel. Perfect for classroom activities, contests, team selection, and decision making.',
				h1: 'Free Online Random Name Picker Wheel',
				subheading: 'The Ultimate Wheel Spinner Tool for Fair Random Selection',
				introText: 'Add names or options to the wheel, click spin, and get a random winner instantly. Perfect for classrooms, contests, games, or making random decisions with complete fairness.',
				keywords: ['random name picker', 'wheel spinner', 'random selection tool'],
				benefits: [
					{
						title: 'Fair Selection',
						content: 'Ensure everyone gets an equal chance to participate with random selection.'
					},
					{
						title: 'Engagement',
						content: 'Keep everyone engaged with the excitement of the wheel spin.'
					}
				],
				useCases: [
					{
						title: 'Classroom Selection',
						content: 'Randomly select participants for activities and discussions.'
					},
					{
						title: 'Fair Decision Making',
						content: 'Use the wheel to make random choices when multiple options are available.'
					}
				],
				faqItems: [
					{
						question: 'How does the random selection work?',
						answer: 'Our wheel spinner uses a cryptographically secure random algorithm to ensure completely unbiased results, giving every name an equal chance of being selected.'
					},
					{
						question: 'Is there a limit to how many names I can add?',
						answer: 'No! You can add as many names or options as you need to the wheel spinner. The wheel will automatically adjust to accommodate all entries.'
					}
				],
				ctaTitle: 'Start Using This Tool Today',
				ctaText: 'Free to use with no registration required. Works on any device!'
			};
		}
		
		// Return the existing data
		return data.pageData;
	}
	
	// Get safe pageData with fallbacks
	const pageData = getSafePageData();

	let winner = null;
	let spinning = false;
	let showWinnerPopup = false;
	let wheelComponent;

	function handleSpin() {
		spinning = true;
		wheelComponent.spin();
	}

	function handleWinner(event) {
		winner = event.detail.winner;
		spinning = false;
		showWinnerPopup = true;
	}

	function loadSampleNames() {
		// Use the page-specific sample names if available, otherwise use default names
		const names = pageData?.sampleNames || [
			'Alex',
			'Jamie',
			'Casey',
			'Jordan',
			'Taylor',
			'Morgan',
			'Riley',
			'Quinn'
		];
		wheelStore.setNames(names);
	}

	onMount(() => {
		// Check for URL params or restore from localStorage
		const storedNames = localStorage.getItem('wheelNames');
		if (storedNames) {
			try {
				const names = JSON.parse(storedNames);
				if (Array.isArray(names) && names.length > 0) {
					wheelStore.setNames(names);
				} else {
					loadSampleNames();
				}
			} catch (e) {
				console.error('Error parsing stored names:', e);
				loadSampleNames();
			}
		} else {
			loadSampleNames();
		}

		// No auto-spin on page load anymore
		// Force a redraw of the wheel after a short delay to ensure it's visible
		setTimeout(() => {
			if (wheelComponent) {
				// This will trigger a redraw without spinning
				const currentNames = [...$wheelStore.names];
				wheelStore.setNames(currentNames);
			}
		}, 200);
	});
</script>

<svelte:head>
	{@html `
  <script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "${pageData?.title || 'Random Name Picker Wheel | Online Spinner Tool'}",
    "description": "${pageData?.metaDescription || 'Free online random name picker wheel. Perfect for classroom activities, contests, team selection, and decision making.'}",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "All",
    "offers": {
      "@type": "Offer",
      "price": "0.00",
      "priceCurrency": "USD"
    },
    "keywords": "${pageData?.keywords ? pageData.keywords.join(', ') : 'random name picker, wheel spinner, random selector'}"
  }
  </script>
  <script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": ${JSON.stringify(
			(pageData?.faqItems && pageData.faqItems.length > 0) ? pageData.faqItems.map((item) => ({
				'@type': 'Question',
				name: item.question,
				acceptedAnswer: {
					'@type': 'Answer',
					text: item.answer
				}
			})) : [
				{
					'@type': 'Question', 
					name: 'How does the random selection work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our wheel spinner uses a cryptographically secure random algorithm to ensure completely unbiased results.'
					}
				}
			]
		)}
  }
  </script>
  `}
	<title>{pageData?.title || 'Random Name Picker Wheel | Free Online Spinner Tool'}</title>
	<meta name="description" content={pageData?.metaDescription || 'Free online random name picker wheel. Perfect for classroom activities, contests, team selection, and decision making.'} />
	<meta name="keywords" content={pageData?.keywords ? pageData.keywords.join(', ') : 'random name picker, wheel spinner, random selector'} />
</svelte:head>

<div class="container">
	<section class="intro">
		<h1>{pageData?.h1 || 'Random Name Picker Wheel'}</h1>
		<p class="subheading">{pageData?.subheading || 'The Ultimate Wheel Spinner Tool for Fair Random Selection'}</p>
		<p>{pageData?.introText || 'Add names or options to the wheel, click spin, and get a random winner instantly. Perfect for classrooms, contests, games, or making random decisions with complete fairness.'}</p>
	</section>

	<div class="wheel-container">
		<div class="wheel-section">
			<Wheel
				bind:this={wheelComponent}
				names={$wheelStore.names}
				colors={$wheelStore.colors}
				on:winner={handleWinner}
			/>

			<div class="controls">
				<button
					class="spin-button"
					on:click={handleSpin}
					disabled={spinning || $wheelStore.names.length < 2}
				>
					{spinning ? 'Spinning...' : 'Spin the Wheel'}
				</button>

				<WinnerPopup {winner} on:close={() => (showWinnerPopup = false)} />

				{#if winner && !showWinnerPopup}
					<div class="winner">
						<h2>Selected: {winner}</h2>
						<div class="winner-name">{winner}</div>
						<button on:click={() => (winner = null)}>Clear</button>
					</div>
				{/if}
			</div>
		</div>

		<div class="settings-section">
			<NameInput />
			<ShareButtons />
		</div>
	</div>

	<section class="benefits">
		<h2>{pageData?.benefitsTitle || 'Benefits'}</h2>
		<div class="benefits-grid">
			{#if pageData?.benefits && pageData.benefits.length > 0}
				{#each pageData.benefits as benefit}
					<div class="benefit">
						<h3>{benefit.title}</h3>
						<p>{benefit.content}</p>
					</div>
				{/each}
			{:else}
				<div class="benefit">
					<h3>Fair Selection</h3>
					<p>Ensure everyone gets an equal chance to participate with random selection.</p>
				</div>
				<div class="benefit">
					<h3>Engagement</h3>
					<p>Keep everyone engaged with the excitement of the wheel spin.</p>
				</div>
			{/if}
		</div>
	</section>

	<section class="use-cases">
		<h2>{pageData?.useCasesTitle || 'Ways to Use This Tool'}</h2>
		<div class="uses-grid">
			{#if pageData?.useCases && pageData.useCases.length > 0}
				{#each pageData.useCases as useCase}
					<div class="use-item">
						<h3>{useCase.title}</h3>
						<p>{useCase.content}</p>
					</div>
				{/each}
			{:else}
				<div class="use-item">
					<h3>Classroom Selection</h3>
					<p>Randomly select participants for activities and discussions.</p>
				</div>
				<div class="use-item">
					<h3>Fair Decision Making</h3>
					<p>Use the wheel to make random choices when multiple options are available.</p>
				</div>
			{/if}
		</div>
	</section>

	{#if pageData?.testimonials && pageData.testimonials.length > 0}
		<section class="testimonials">
			<h2>{pageData?.testimonialsTitle || 'What Users Say'}</h2>
			<div class="testimonials-grid">
				{#each pageData.testimonials as testimonial}
					<div class="testimonial">
						<p class="quote">{testimonial.quote}</p>
						<p class="author">- {testimonial.author}</p>
					</div>
				{/each}
			</div>
		</section>
	{/if}

	<section class="faq">
		<h2>Frequently Asked Questions</h2>
		<div class="faq-items">
			{#if pageData?.faqItems && pageData.faqItems.length > 0}
				{#each pageData.faqItems as item}
					<div class="faq-item">
						<h3>{item.question}</h3>
						<p>{item.answer}</p>
					</div>
				{/each}
			{:else}
				<div class="faq-item">
					<h3>How does the random selection work?</h3>
					<p>Our wheel spinner uses a cryptographically secure random algorithm to ensure completely unbiased results, giving every name an equal chance of being selected.</p>
				</div>
				<div class="faq-item">
					<h3>Is there a limit to how many names I can add?</h3>
					<p>No! You can add as many names or options as you need to the wheel spinner. The wheel will automatically adjust to accommodate all entries.</p>
				</div>
			{/if}
		</div>
	</section>

	{#if pageData?.relatedPages && pageData.relatedPages.length > 0}
		<section class="related-pages">
			<h2>Related Tools</h2>
			<div class="related-links">
				{#each pageData.relatedPages as page}
					<a href="/{page.type}/{page.slug}" class="related-link">{page.linkText}</a>
				{/each}
			</div>
		</section>
	{/if}

	<section class="cta">
		<h2>{pageData?.ctaTitle || 'Start Using This Tool Today'}</h2>
		<p>{pageData?.ctaText || 'Free to use with no registration required. Works on any device!'}</p>
	</section>
</div>

<style>
	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
	}

	.intro {
		text-align: center;
		margin: 2rem 0;
	}

	h1 {
		font-size: 2.5rem;
		margin-bottom: 1rem;
		color: #2c3e50;
	}

	.subheading {
		font-size: 1.5rem;
		margin-bottom: 1rem;
		color: #3498db;
		font-weight: 500;
	}

	.wheel-container {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		margin: 2rem 0;
	}

	@media (min-width: 768px) {
		.wheel-container {
			flex-direction: row;
		}

		.wheel-section {
			flex: 3;
		}

		.settings-section {
			flex: 2;
		}
	}

	.wheel-section {
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.controls {
		margin-top: 1.5rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
	}

	.spin-button {
		padding: 0.75rem 2rem;
		font-size: 1.25rem;
		background-color: #4caf50;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		transition: background-color 0.3s;
	}

	.spin-button:hover:not(:disabled) {
		background-color: #45a049;
	}

	.spin-button:disabled {
		background-color: #cccccc;
		cursor: not-allowed;
	}

	.winner {
		background-color: #f8f9fa;
		border: 1px solid #dee2e6;
		border-radius: 8px;
		padding: 1rem;
		text-align: center;
	}

	.winner-name {
		font-size: 1.5rem;
		font-weight: bold;
		margin: 0.5rem 0;
		color: #333;
	}

	section {
		margin: 3rem 0;
	}

	h2 {
		text-align: center;
		margin-bottom: 1.5rem;
		color: #2c3e50;
		font-size: 2rem;
	}

	.benefits-grid,
	.uses-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
		gap: 1.5rem;
		margin-top: 1.5rem;
	}

	.benefit,
	.use-item,
	.faq-item {
		background-color: #f8f9fa;
		border-radius: 8px;
		padding: 1.5rem;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
	}

	.benefit h3,
	.use-item h3 {
		color: #3498db;
		margin-bottom: 0.75rem;
	}

	.testimonials-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 1.5rem;
		margin-top: 1.5rem;
	}

	@media (min-width: 768px) {
		.testimonials-grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	.testimonial {
		background-color: #f8f9fa;
		border-radius: 8px;
		padding: 1.5rem;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		position: relative;
	}

	.quote {
		font-style: italic;
		margin-bottom: 1rem;
		position: relative;
	}

	.quote::before {
		content: '"';
		font-size: 3rem;
		position: absolute;
		top: -1.5rem;
		left: -1rem;
		color: #e0e0e0;
		z-index: 0;
	}

	.author {
		font-weight: bold;
		text-align: right;
		color: #555;
	}

	.faq-items {
		display: grid;
		grid-template-columns: 1fr;
		gap: 1.5rem;
		margin-top: 1.5rem;
	}

	@media (min-width: 768px) {
		.faq-items {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	.faq-item h3 {
		color: #2c3e50;
		margin-bottom: 0.75rem;
		font-size: 1.1rem;
	}

	.related-pages {
		background-color: #f0f7ff;
		padding: 2rem;
		border-radius: 8px;
		text-align: center;
	}

	.related-links {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 1rem;
		margin-top: 1rem;
	}

	.related-link {
		display: inline-block;
		background-color: #3498db;
		color: white;
		padding: 0.5rem 1rem;
		border-radius: 4px;
		text-decoration: none;
		transition: background-color 0.3s;
	}

	.related-link:hover {
		background-color: #2980b9;
	}

	.cta {
		text-align: center;
		background-color: #f0f7ff;
		padding: 2rem;
		border-radius: 8px;
		margin: 3rem 0;
	}

	.cta h2 {
		color: #2c3e50;
		margin-bottom: 1rem;
	}

	.cta p {
		margin-bottom: 0.5rem;
		font-size: 1.1rem;
	}

	@media (max-width: 767px) {
		h1 {
			font-size: 1.8rem;
			margin-bottom: 0.5rem;
		}

		.intro {
			margin: 1rem 0;
		}

		.subheading {
			font-size: 1.2rem;
			margin-bottom: 0.5rem;
		}

		h2 {
			font-size: 1.5rem;
		}

		.benefit h3,
		.use-item h3,
		.faq-item h3 {
			font-size: 1.1rem;
			margin-bottom: 0.5rem;
		}

		.wheel-container {
			gap: 1rem;
			margin: 1rem 0;
		}

		.controls {
			margin-top: 1rem;
		}

		section {
			margin: 1.5rem 0;
		}

		.benefit,
		.use-item,
		.faq-item,
		.testimonial {
			padding: 1rem;
		}
	}
</style>
